import { Slider } from '@miblanchard/react-native-slider';
import { useIsFocused } from '@react-navigation/native';
import { AVPlaybackStatus, Audio as ExpoAudio } from 'expo-av';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';

import { Icons } from 'components/Icons';
import { usePlayer } from 'components/Player/context';

interface AudioBarProps {
  audioUrl: string;
  openVideo?: boolean;
  isLive?: boolean;
  isPlaying: boolean | undefined;
  setIsPlaying: Dispatch<SetStateAction<boolean | undefined>>;
}

export default function AudioBar({
  audioUrl,
  isLive = false,
  openVideo = false,
  isPlaying = false,
  setIsPlaying,
}: AudioBarProps) {
  const { sound, setSound } = usePlayer();
  const isFocused = useIsFocused();

  const [isMute, setIsMute] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiveUpdated, setIsLiveUpdated] = useState(true);

  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const activeUrlRef = useRef(audioUrl);
  const isTransitioningRef = useRef(false);
  const lastProgressUpdateRef = useRef(0);
  const isSeekingRef = useRef(false);

  const formatTime = (millis: number) => {
    if (!millis || millis < 0) return '00:00';
    const minutes = Math.floor(millis / 60000);
    const seconds = Math.floor((millis % 60000) / 1000);
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const onPlaybackStatusUpdate = (status: AVPlaybackStatus) => {
    if (status.isLoaded) {
      if (!isTransitioningRef.current) {
        setIsPlaying(status.isPlaying);
      }

      if (!isLive && !isSeekingRef.current) {
        const now = Date.now();
        if (
          now - lastProgressUpdateRef.current >= 1000 ||
          status.positionMillis === status.durationMillis
        ) {
          setPosition(status.positionMillis);
          setDuration(status.durationMillis || 0);
          lastProgressUpdateRef.current = now;
        }
      }
    } else if (status.error) {
      console.error(`Playback error: ${status.error}`);
    }
  };

  const handlePlayPause = async () => {
    if (!sound) return;

    isTransitioningRef.current = true;
    const nextPlayState = !isPlaying;
    setIsPlaying(nextPlayState);

    try {
      if (!nextPlayState) {
        await sound.pauseAsync();
        setIsLiveUpdated(false);
      } else {
        await sound.playAsync();
      }
    } catch (err) {
      console.error('Failed to toggle playback:', err);
      setIsPlaying(!nextPlayState);
    } finally {
      setTimeout(() => {
        isTransitioningRef.current = false;
      }, 300);
    }
  };

  const changeVolume = async (newVolume: number) => {
    setVolume(newVolume);
    if (sound) {
      await sound.setVolumeAsync(newVolume);
    }
  };

  const handleSliderSlidingComplete = async (value: number[]) => {
    if (sound) {
      try {
        const targetPosition = value[0];
        await sound.setPositionAsync(targetPosition);
        setPosition(targetPosition);
      } catch (error) {
        console.error('Seeking error:', error);
      } finally {
        isSeekingRef.current = false;
      }
    }
  };

  const handleSliderSlidingStart = () => {
    isSeekingRef.current = true;
  };

  const handleLiveSync = async () => {
    if (sound && isLive) {
      try {
        await sound.stopAsync();
        await sound.playAsync();
        setIsLiveUpdated(true);
        setIsPlaying(true);
      } catch (e) {
        console.error('Failed syncing live status:', e);
      }
    }
  };

  const handleMute = async () => {
    if (sound) {
      await sound.setIsMutedAsync(!isMute);
      setIsMute(!isMute);
    }
  };

  useEffect(() => {
    if (!isFocused) return;

    let isCurrentEffectActive = true;
    activeUrlRef.current = audioUrl;

    const manageAudioLifecycle = async () => {
      setIsLoading(true);

      if (sound) {
        try {
          await sound.stopAsync();
          await sound.unloadAsync();
        } catch (e) {
          console.log('Error unloading previous sound:', e);
        }
      }

      if (!isCurrentEffectActive) return;
      setSound(null);

      const isMp3 = !!audioUrl?.includes('.mp3');
      if (!isMp3 || !audioUrl) {
        setIsLoading(false);
        return;
      }

      try {
        const { sound: newSound } = await ExpoAudio.Sound.createAsync(
          { uri: audioUrl },
          {
            shouldPlay: true,
            volume: volume,
            progressUpdateIntervalMillis: 1000,
          },
          onPlaybackStatusUpdate
        );

        if (isCurrentEffectActive && activeUrlRef.current === audioUrl) {
          setSound(newSound);
          setIsLoading(false);
        } else {
          await newSound.unloadAsync();
        }
      } catch (error) {
        console.error('Error creating new sound instance:', error);
        if (isCurrentEffectActive) setIsLoading(false);
      }
    };

    manageAudioLifecycle();

    return () => {
      isCurrentEffectActive = false;
    };
  }, [audioUrl, isFocused, openVideo]);

  useEffect(() => {
    return () => {
      if (!isFocused && sound) {
        sound.stopAsync().catch(() => {});
        sound.unloadAsync().catch(() => {});
        setSound(null);
      }
    };
  }, [isFocused]);

  const handleVideo = async () => {
    if (!sound) return;

    try {
      if (openVideo) {
        await sound.stopAsync();
      } else {
        setTimeout(async () => {
          await sound.playAsync();
        }, 500);
      }

      setIsPlaying(!openVideo);
    } catch (error) {
      console.error('Failed to sync audio with video state:', error);
    }
  };

  useEffect(() => {
    handleVideo();
  }, [openVideo, sound]);

  const TogglePlayPauseIcon = isPlaying ? Icons.SolidPause : Icons.Play;
  const ToggleVolumeIcon = isMute ? Icons.VolumeSlash : Icons.VolumeHigh;

  const display = openVideo || Platform.isTV ? 'none' : 'flex';

  if (isLoading) {
    return (
      <View style={[styles.container, { display }]}>
        <ActivityIndicator color="#fff" size="small" />
      </View>
    );
  }

  return (
    <View style={[styles.container, { display }]}>
      <Pressable onPress={handlePlayPause} style={styles.controlButton}>
        <TogglePlayPauseIcon
          width={calcSizeForTv(25).toString()}
          height={calcSizeForTv(25).toString()}
        />
      </Pressable>

      {isLive ? (
        <Pressable style={styles.centerContainer} onPress={handleLiveSync}>
          <View style={[styles.liveDot, { backgroundColor: isLiveUpdated ? '#2a7596' : '#555' }]} />
          <Text style={styles.liveText}>LIVE</Text>
        </Pressable>
      ) : (
        <View style={styles.centerContainer}>
          <Text style={styles.timeText}>{formatTime(position)}</Text>

          <View style={styles.sliderWrapper}>
            <Slider
              minimumValue={0}
              maximumValue={duration || 1}
              value={position}
              onSlidingStart={handleSliderSlidingStart}
              onSlidingComplete={handleSliderSlidingComplete}
              minimumTrackTintColor="#2a7596"
              maximumTrackTintColor="#333"
              thumbTintColor="#fff"
              containerStyle={styles.sliderContainer}
            />
          </View>

          <Text style={styles.timeText}>{formatTime(duration)}</Text>
        </View>
      )}

      <View style={styles.volumeContainer}>
        <Pressable onPress={handleMute}>
          <ToggleVolumeIcon
            width={calcSizeForTv(20).toString()}
            height={calcSizeForTv(20).toString()}
          />
        </Pressable>
        {!isMute && !Platform.isMobileWeb() ? (
          <View style={styles.volumeBarsRow}>
            {[0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0].map((level, index) => {
              const isActive = volume >= level;
              return (
                <Pressable
                  key={index}
                  onPress={() => changeVolume(level)}
                  style={[
                    styles.volumeBarChunk,
                    { backgroundColor: isActive ? '#2a7596' : '#222' },
                  ]}
                />
              );
            })}
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#000000',
    paddingVertical: 14,
    paddingHorizontal: Platform.isMobileWeb() ? 10 : 20,
    width: '100%',
    maxHeight: 50,
    justifyContent: 'center',
  },
  controlButton: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
  },
  centerContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  sliderWrapper: {
    flex: 1,
    marginHorizontal: 12,
    justifyContent: 'center',
  },
  sliderContainer: {
    height: 40,
  },
  liveDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  liveText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  timeText: {
    color: '#fff',
    fontSize: 14,
    fontVariant: ['tabular-nums'],
  },
  volumeContainer: {
    flexDirection: 'row',
    width: Platform.isMobileWeb() ? 40 : 102,
    justifyContent: Platform.isMobileWeb() ? 'center' : 'space-between',
  },
  volumeBarsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
  },
  volumeBarChunk: {
    width: 5,
    height: 12,
  },
});
